/**
 * Faz 6 §64 (M35) — ALWAYS-VALID SIRALI TEST (iki-oranlı mSPRT, normal yaklaşım).
 *
 * NEDEN sıralı (KARAR 1, §61.2): operatör panele her gün bakar; sabit-örneklem
 * p'si "anlamlı görününce durdurma" (peeking) altında geçersizdir — yanlış kazanan
 * ilanının ana mekanizması budur. mSPRT'nin p'si HER BAKIŞTA geçerlidir; bedeli,
 * aynı örneklemde sabit teste göre daha geç anlamlılıktır (bilinçli takas).
 *
 * Matematik R19/R20 disipliniyle elle yazılmıştır: adlandırılmış sabitler, dejenere
 * girdiler için AÇIK erken dönüş, kütüphane yok. Sayısal davranış
 * `contracts/vectors/experiment-stats.json` ile sabitlenir (üretici, formülü
 * BAĞIMSIZ ikinci bir yazımla hesaplayıp karşılaştırır — SigV4 vektör ruhu).
 *
 * Formüller (iki kol: control C, varyant V; p̂=dönüşüm oranı):
 *   d    = p̂V − p̂C
 *   se²  = p̂C(1−p̂C)/nC + p̂V(1−p̂V)/nV          (havuzlanmamış)
 *   Λ    = √(se²/(se²+τ²)) · exp( d²τ² / (2·se²·(se²+τ²)) )
 *   p    = min(1, 1/Λ)                            (always-valid; motor MONOTON tutar)
 *   CI   = d ± √( se²·(se²+τ²)/τ² · ln((se²+τ²)/(se²·α²)) )   (güven dizisi)
 */

/** §64.2 — karışım ölçeği: aranan MUTLAK CR farkı (MDE). τ'yu MDE'ye bağlamak,
 *  o etki büyüklüğünde gücü maksimize eden standart seçimdir. */
export const EXP_DEFAULT_TAU = 0.02;

/** §61.5 — always-valid anlamlılık eşiği (iki kol; >2 kolda Bonferroni, §64.5). */
export const EXP_DEFAULT_ALPHA = 0.05;

/** §64.2 — kol başına asgari oturum: altında değerlendirme YOK ("veri toplanıyor"). */
export const EXP_MIN_SESSIONS_PER_ARM = 100;

/** §64.4 — güç planı z sabitleri (erf/ppf YAZILMAZ — iki sabit için kütüphane olmaz). */
export const Z_ALPHA_05_TWO_SIDED = 1.959964;
export const Z_POWER_80 = 0.841621;

export interface PairInput {
  /** control kolu: oturum + dönüşüm */
  nC: number;
  cC: number;
  /** varyant kolu */
  nV: number;
  cV: number;
  /** karışım ölçeği (MDE, mutlak) — verilmezse EXP_DEFAULT_TAU */
  tau?: number;
  /** anlamlılık eşiği (Bonferroni SONRASI) — verilmezse EXP_DEFAULT_ALPHA */
  alpha?: number;
  /** kol başına asgari oturum — verilmezse EXP_MIN_SESSIONS_PER_ARM */
  minPerArm?: number;
}

export interface PairResult {
  /** mutlak fark p̂V − p̂C */
  d: number;
  se: number;
  /** always-valid p (bu bakış; monotonluk ÇAĞIRANIN sorumluluğu — motor min alır) */
  pAlways: number;
  /** always-valid güven dizisi uçları (mutlak fark ölçeğinde) */
  ciLow: number;
  ciHigh: number;
  /** p̂C > 0 ise oransal lift (d/p̂C) üçlüsü; değilse null (mutlak fark raporlanır) */
  liftLow: number | null;
  liftMid: number | null;
  liftHigh: number | null;
  /** pAlways < alpha */
  significant: boolean;
}

/**
 * §64.2 — tek varyant-control çifti değerlendirmesi.
 * null = değerlendirme YAPILAMAZ (veri az ya da metrik dejenere) — "sonuç yok",
 * "fark yok" DEĞİL; çağıran bu ayrımı korumak zorundadır.
 */
export function evaluatePair(input: PairInput): PairResult | null {
  const tau = input.tau ?? EXP_DEFAULT_TAU;
  const alpha = input.alpha ?? EXP_DEFAULT_ALPHA;
  const minPerArm = input.minPerArm ?? EXP_MIN_SESSIONS_PER_ARM;

  const { nC, cC, nV, cV } = input;
  if (!isFinite(nC) || !isFinite(nV) || nC < minPerArm || nV < minPerArm) return null;
  if (cC < 0 || cV < 0 || cC > nC || cV > nV) return null;
  if (!(tau > 0) || !(alpha > 0) || alpha >= 1) return null;

  const pC = cC / nC;
  const pV = cV / nV;
  const se2 = (pC * (1 - pC)) / nC + (pV * (1 - pV)) / nV;
  // Dejenere: iki kolda da dönüşüm hiç yok/hep var → varyans 0, metrik anlamsız.
  if (!(se2 > 0)) return null;

  const d = pV - pC;
  const tau2 = tau * tau;
  const shrink = se2 / (se2 + tau2);
  const lambda = Math.sqrt(shrink) * Math.exp((d * d * tau2) / (2 * se2 * (se2 + tau2)));
  const pAlways = Math.min(1, 1 / lambda);

  const radius = Math.sqrt(((se2 * (se2 + tau2)) / tau2) * Math.log((se2 + tau2) / (se2 * alpha * alpha)));
  const ciLow = d - radius;
  const ciHigh = d + radius;

  const lift = pC > 0 ? { low: ciLow / pC, mid: d / pC, high: ciHigh / pC } : null;

  return {
    d,
    se: Math.sqrt(se2),
    pAlways,
    ciLow,
    ciHigh,
    liftLow: lift === null ? null : lift.low,
    liftMid: lift === null ? null : lift.mid,
    liftHigh: lift === null ? null : lift.high,
    significant: pAlways < alpha,
  };
}

/**
 * §64.4 — PLANLAMA aracı (durdurma mekanizması DEĞİL): sabit-örneklem yaklaşımıyla
 * kol başına gereken oturum (α=0.05 çift yönlü, güç %80).
 * Dejenere girdi (p̄∉(0,1), mde≤0) → null.
 */
export function requiredSamplePerArm(pBar: number, mdeAbs: number): number | null {
  if (!(pBar > 0) || !(pBar < 1) || !(mdeAbs > 0)) return null;
  const z = Z_ALPHA_05_TWO_SIDED + Z_POWER_80;
  return Math.ceil((2 * pBar * (1 - pBar) * z * z) / (mdeAbs * mdeAbs));
}
